<template>
  <div class="my-tasks-panel">
    <div class="panel-header">
      <div>
        <h2>Minhas Tarefas</h2>
        <p class="panel-subtitle">Tarefas em andamento hoje — {{ todayLabel }}</p>
      </div>
    </div>

    <div v-if="loading" class="empty-state">
      <p>Carregando tarefas...</p>
    </div>

    <div v-else-if="projects.length === 0" class="empty-state">
      <p>Nenhum projeto disponivel.</p>
    </div>

    <div v-else-if="allTodayTasks.length === 0" class="empty-state">
      <p>Nenhuma tarefa para hoje.</p>
    </div>

    <div v-else class="tasks-container">
      <div v-for="group in tasksByProject" :key="group.projectId" class="project-section">
        <div class="project-header" @click="$emit('view-project', group.projectId)" role="button" tabindex="0">
          <h3 class="project-title">{{ group.projectName }}</h3>
          <span class="task-count">{{ group.tasks.length }} tarefa{{ group.tasks.length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="tasks-list">
          <div v-for="task in group.tasks" :key="task.id" class="task-row">
            <div class="task-info">
              <span class="task-name">{{ task.title }}</span>
              <span class="task-dates">{{ formatDate(task.plannedStart) }} — {{ formatDate(task.plannedEnd) }}</span>
            </div>
            <div class="task-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: taskProgress(task) + '%' }"></div>
              </div>
              <span class="progress-label">{{ taskProgress(task) }}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, type PropType } from 'vue'
import { apiFetch } from '@/store/api'
import type { Project } from '@/store/projects'

interface TaskProgressUpdate {
  progressDate: string
  progressPercent?: number
}

interface ProjectTask {
  id: string
  title: string
  assigneeUsername: string
  plannedStart: string
  plannedEnd: string
  progressUpdates?: TaskProgressUpdate[]
}

export default defineComponent({
  name: 'MyTasksPanel',
  props: {
    projects: {
      type: Array as PropType<Project[]>,
      required: true
    },
    currentUsername: {
      type: String,
      default: ''
    }
  },
  emits: ['view-project'],
  setup(props) {
    const loading = ref(false)
    const tasksByProjectId = ref<Record<string, ProjectTask[]>>({})

    const today = new Date().toISOString().slice(0, 10)

    const todayLabel = computed(() =>
      new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
    )

    const loadTasks = async () => {
      if (props.projects.length === 0) return
      loading.value = true
      try {
        const results = await Promise.all(
          props.projects.map(async (proj) => {
            try {
              const tasks = await apiFetch<ProjectTask[]>(`/projects/${encodeURIComponent(proj.id)}/tasks`)
              return { projectId: proj.id, tasks: Array.isArray(tasks) ? tasks : [] }
            } catch {
              return { projectId: proj.id, tasks: [] }
            }
          })
        )
        const map: Record<string, ProjectTask[]> = {}
        results.forEach(({ projectId, tasks }) => { map[projectId] = tasks })
        tasksByProjectId.value = map
      } finally {
        loading.value = false
      }
    }

    const allTodayTasks = computed(() => {
      const username = props.currentUsername
      const result: { projectId: string; task: ProjectTask }[] = []
      for (const proj of props.projects) {
        const tasks = tasksByProjectId.value[proj.id] || []
        for (const task of tasks) {
          if (username && task.assigneeUsername !== username) continue
          if (task.plannedStart > today || task.plannedEnd < today) continue
          result.push({ projectId: proj.id, task })
        }
      }
      return result
    })

    const tasksByProject = computed(() => {
      const groups: { projectId: string; projectName: string; tasks: ProjectTask[] }[] = []
      for (const proj of props.projects) {
        const tasks = allTodayTasks.value
          .filter((e) => e.projectId === proj.id)
          .map((e) => e.task)
        if (tasks.length > 0) {
          groups.push({ projectId: proj.id, projectName: proj.name, tasks })
        }
      }
      return groups
    })

    const taskProgress = (task: ProjectTask) => {
      const updates = task.progressUpdates || []
      if (updates.length === 0) return 0
      const last = updates[updates.length - 1]
      return Math.round(Number(last.progressPercent ?? 0))
    }

    const formatDate = (value: string) => {
      if (!value) return '-'
      const [year, month, day] = value.split('-')
      return `${day}/${month}/${year}`
    }

    onMounted(() => { loadTasks() })

    return { loading, todayLabel, allTodayTasks, tasksByProject, taskProgress, formatDate }
  }
})
</script>

<style scoped lang="scss">
.my-tasks-panel {
  padding: 20px;
}

.panel-header {
  margin-bottom: 24px;
  border-bottom: 1px solid var(--border, rgba(0, 0, 0, 0.08));
  padding-bottom: 16px;

  h2 {
    margin: 0 0 4px;
    font-size: 20px;
    font-weight: 600;
    color: var(--ink);
  }
}

.panel-subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--ink-muted);
  text-transform: capitalize;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--ink-muted);
  font-size: 14px;

  p { margin: 0; }
}

.tasks-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.project-section {
  border: 1px solid var(--border, rgba(0, 0, 0, 0.08));
  border-radius: 8px;
  overflow: hidden;
  background: var(--surface, #f9f9f9);
}

.project-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--card, #fff);
  border-bottom: 1px solid var(--border, rgba(0, 0, 0, 0.08));
  cursor: pointer;

  &:hover { background: rgba(0, 0, 0, 0.02); }
}

.project-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}

.task-count {
  font-size: 12px;
  color: var(--ink-muted);
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 8px;
  border-radius: 10px;
}

.tasks-list {
  display: flex;
  flex-direction: column;
}

.task-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  gap: 16px;
  border-bottom: 1px solid var(--border, rgba(0, 0, 0, 0.05));

  &:last-child { border-bottom: none; }
}

.task-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.task-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-dates {
  font-size: 11px;
  color: var(--ink-muted);
}

.task-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.progress-bar {
  width: 80px;
  height: 6px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--accent, #0f4c81);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-muted);
  min-width: 28px;
  text-align: right;
}
</style>
